import { PrimaryButton, Stack } from "@fluentui/react";
import { Formik } from "formik";
import * as React from "react";
import { Loading } from "../../../services/Loading";
import { ICustomerReport } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { ToastContext } from "../../common/ToastProvider";
import {
  DefaultButton,
  IBasePickerSuggestionsProps,
  IStackTokens,
  ITag,
  TagPicker,
  Text,
  TextField,
} from "office-ui-fabric-react";
import * as yup from "yup";
import {
  getCemApprovedDcForCustomer,
  getCustomerReportEmail,
  sendReportToCustomer,
} from "../../../services/customers/CustomerService";

const schema = yup.object().shape({
  to: yup.string().required("required"),
  cc: yup.string().required("required"),
  datacenterIds: yup.array().required("select at least one"),
});

const wrapStackTokens: IStackTokens = { childrenGap: 16 };

export interface ManageSendEmailProps {
  handleClose: Function;
  customerReport: ICustomerReport;
  datacenterId: string;
  customerId: string;
}

const ManageSendEmail: React.FC<ManageSendEmailProps> = ({
  handleClose,
  customerReport,
  datacenterId,
  customerId,
}) => {
  //custom hooks
  const { showToast } = React.useContext(ToastContext);
  // local state
  const [isLoading, setLoading] = React.useState(false);
  const [state, setState] = React.useState({
    datacenterOptions: [],
    selectedDatacenter: [],
  } as {
    datacenterOptions: ITag[];
    selectedDatacenter: ITag[];
  });
  const [customerReportEmail, setCustomerReportEmail] = React.useState({
    to: "",
    cc: "",
    datacenterIds: [datacenterId],
    //   customerReportId: customerReport._id,
  } as { to: string; cc: string; datacenterIds: string[] })

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      setLoading(true);
      await getCustomerReportEmailDetails();
      await getDatacenterForCustomers();
      setLoading(false);
    };
    asyncFunc();
  }, []);

  const getDatacenterForCustomers = async () => {
    // setLoading(true);
    const result = await getCemApprovedDcForCustomer({
      customerId,
    });
    if (result?.success) {
      let datacenterOptions: ITag[] = [];
      let selectedDatacenter: ITag[] = [];
      // let datacenterIds: string[] = [];

      result.data.forEach((d) => {
        if (d._id === datacenterId) {
          selectedDatacenter.push({
            key: d._id as string,
            name: d.name,
          });
          // datacenterIds.push(d._id);
        }
        datacenterOptions.push({
          key: d._id as string,
          name: d.name,
        });
      });
      setState({
        ...state,
        datacenterOptions,
        selectedDatacenter,
      });
    } else {
      showToast(result?.message || "Error while getting datacenters", "error");
    }
    // setLoading(false);
  };

  const getCustomerReportEmailDetails = async () => {
    const result = await getCustomerReportEmail({ datacenterId, customerId });
    if (result?.success) {
      if (result.data) setCustomerReportEmail({ ...customerReportEmail, cc: result.data.cc, to: result.data.to, datacenterIds: [datacenterId] });
    } else {
      showToast(result?.message || "Error while getting saved emails", "error");
    }
  }

  const handleDialogClose = (data?: ICustomerReport) => {
    handleClose(data);
  };
  // const handleDialogClose = (isOpen: boolean) => {
  //   handleClose(isOpen);
  // };

  const datacenterPickerSuggestionsProps: IBasePickerSuggestionsProps = {
    noResultsFoundText: "No datacenter found",
  };

  const filterSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    let allDataCenterList = [...state.datacenterOptions];

    // state.selectedDatacenter.forEach((item) => {
    //   allDataCenterList = allDataCenterList.filter(
    //     (ele) => ele.key !== item.key
    //   );
    // });

    return filterText
      ? allDataCenterList.filter(
        (tag) =>
          tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0 &&
          !listContainsTagList(tag, tagList)
      )
      : [];
  };

  const showAllTags = (selectedItems?: ITag[] | undefined) => {
    let allDataCenterList = [...state.datacenterOptions];

    return removeDuplicates(allDataCenterList, selectedItems);
  };

  function removeDuplicates(tagList: ITag[], tags?: ITag[]) {
    return tagList.filter((tag) => !listContainsTagList(tag, tags));
  }

  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length || tagList.length === 0) {
      return false;
    }
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };

  return (
    <React.Fragment>
      {isLoading && <Loading />}
      <CustomDrawer
        title={"Send Email to Customer"}
        handelOpen={true}
        onClose={() => handleDialogClose()}
      // onClose={() => handleDialogClose(false)}
      >
        <Stack tokens={childrenGap}>
          <Formik
            enableReinitialize
            validationSchema={schema}
            // initialValues={{
            //   to: "",
            //   cc: "",
            //   datacenterIds: [datacenterId],
            //   //   customerReportId: customerReport._id,
            // }}
            initialValues={customerReportEmail}
            onSubmit={async (
              values: { to: string; cc: string; datacenterIds: string[] },
              { setSubmitting }
            ) => {
              setLoading(true);
              let obj: any = { ...values };
              obj["customerReportId"] = customerReport._id;
              const result = await sendReportToCustomer(obj);
              if (result?.success) {
                setLoading(false);
                showToast("Customer will receive email shortly", "success");
                handleDialogClose(result.data);
                // handleDialogClose(false);
              } else {
                showToast(
                  result?.message || "Error while sending email to customer",
                  "error"
                );
              }
              setLoading(false);
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={wrapStackTokens}>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="to"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.to}
                      label="To"
                      placeholder="type multiple emails separated by comma (,)"
                      errorMessage={errors.to && touched.to ? errors.to : ""}
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-textfield">
                    <TextField
                      name="cc"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.cc}
                      label="CC"
                      placeholder="type multiple emails separated by comma (,)"
                      errorMessage={errors.cc && touched.cc ? errors.cc : ""}
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-picker">
                    <TagPicker
                      selectedItems={state.selectedDatacenter}
                      removeButtonAriaLabel="Remove"
                      onResolveSuggestions={filterSuggestedTags as any}
                      pickerSuggestionsProps={datacenterPickerSuggestionsProps}
                      onEmptyResolveSuggestions={showAllTags}
                      inputProps={{
                        placeholder: "Select a particular datacenter",
                      }}
                      onChange={(items: any) => {
                        let datacenterIds: string[] = [];
                        items.forEach((i: any) => {
                          datacenterIds.push(i.key);
                        });
                        setFieldValue("datacenterIds", datacenterIds);
                        setState({ ...state, selectedDatacenter: items });
                      }}
                    />
                    {errors.datacenterIds && touched.datacenterIds && (
                      <Text className="error-msg">{errors.datacenterIds}</Text>
                    )}
                  </Stack.Item>
                </Stack>
                <Stack.Item>
                  <Stack
                    horizontal
                    wrap
                    tokens={wrapStackTokens}
                    horizontalAlign="end"
                  >
                    <DefaultButton
                      text="Cancel"
                      onClick={() => handleDialogClose()}
                      // onClick={() => handleDialogClose(false)}
                      className="borderless-btn"
                    />
                    <PrimaryButton type="submit" text={"Send Email"} />
                  </Stack>
                </Stack.Item>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageSendEmail;
